import { useEffect, useState } from "react";
import "./index.css";

type WakeLockState = "active" | "requesting" | "unavailable";

interface ScreenWakeLockSentinel extends EventTarget {
  released: boolean;
  release: () => Promise<void>;
}

interface WakeLockNavigator extends Navigator {
  wakeLock?: {
    request: (type: "screen") => Promise<ScreenWakeLockSentinel>;
  };
}

function useScreenWakeLock() {
  const [state, setState] = useState<WakeLockState>("requesting");

  useEffect(() => {
    const wakeLock = (navigator as WakeLockNavigator).wakeLock;
    let sentinel: ScreenWakeLockSentinel | null = null;
    let requesting = false;
    let disposed = false;

    if (!wakeLock) {
      setState("unavailable");
      return;
    }

    const acquire = async () => {
      if (requesting || sentinel || document.visibilityState !== "visible") return;

      requesting = true;
      setState("requesting");

      try {
        const nextSentinel = await wakeLock.request("screen");

        if (disposed) {
          await nextSentinel.release();
          return;
        }

        sentinel = nextSentinel;
        setState("active");
        nextSentinel.addEventListener(
          "release",
          () => {
            if (sentinel === nextSentinel) sentinel = null;
            if (!disposed) setState("unavailable");
          },
          { once: true },
        );
      } catch {
        if (!disposed) setState("unavailable");
      } finally {
        requesting = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (sentinel && !sentinel.released) void sentinel.release();
    };
  }, []);

  return state;
}

function useCurrentTime() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const update = () => {
      if (timeout) clearTimeout(timeout);

      const currentTime = new Date();
      setNow(currentTime);

      // Align updates with the wall-clock second instead of accumulating drift.
      timeout = setTimeout(update, 1000 - currentTime.getMilliseconds());
    };

    const resync = () => {
      if (document.visibilityState === "visible") update();
    };

    update();
    document.addEventListener("visibilitychange", resync);

    return () => {
      if (timeout) clearTimeout(timeout);
      document.removeEventListener("visibilitychange", resync);
    };
  }, []);

  return now;
}

const twoDigits = (value: number) => value.toString().padStart(2, "0");

export function App() {
  const now = useCurrentTime();
  const wakeLockState = useScreenWakeLock();
  const hours = twoDigits(now.getHours());
  const minutes = twoDigits(now.getMinutes());
  const seconds = twoDigits(now.getSeconds());
  const secondProgress = now.getSeconds() / 60;

  const day = new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(now);

  return (
    <main className="clock" aria-label={`Current time ${hours}:${minutes}:${seconds}`}>
      <header className="clock__header">
        <span className="clock__brand">Chronofit</span>
        <div className="clock__meta">
          <span className={`clock__wake-lock is-${wakeLockState}`}>
            <span className="clock__wake-lock-dot" aria-hidden="true" />
            {wakeLockState === "active"
              ? "Screen awake"
              : wakeLockState === "requesting"
                ? "Keeping screen awake…"
                : "Screen wake unavailable"}
          </span>
          <span className="clock__date">{day}</span>
        </div>
      </header>

      <section className="clock__face" aria-hidden="true">
        <div className="clock__hours">
          <span>{hours}</span>
          <span className="clock__colon">:</span>
          <span>{minutes}</span>
        </div>

        <div
          className="clock__seconds-ring"
          style={{ "--second-progress": secondProgress } as React.CSSProperties}
        >
          <div className="clock__seconds-inner">
            <span className="clock__seconds-label">seconds</span>
            <span className="clock__seconds">{seconds}</span>
          </div>
        </div>
      </section>

      <div className="clock__minute-track" aria-hidden="true">
        {Array.from({ length: 60 }, (_, second) => (
          <span
            className={second <= now.getSeconds() ? "is-past" : undefined}
            key={second}
          />
        ))}
      </div>
    </main>
  );
}

export default App;
